// fitToScreen — pure helper som beräknar { scale, scrollTo } för att
// passa innehåll i viewport. Klampar ALLTID inom [zoomMin, zoomMax]
// (tier-range får aldrig överskridas). Om innehållet inte ryms vid
// zoomMin → scale = zoomMin och centroid hamnar i mitten av viewport
// så user scrollar resten.

import type { WorkflowBlock, BlockGroup, BlockDimensions } from '@/types/workflow';

export interface ContentBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  /** Bredden av bounding-box (maxX - minX). */
  width: number;
  /** Höjden av bounding-box (maxY - minY). */
  height: number;
  /** Geometrisk mittpunkt — används som scroll-target när scale clamp:as. */
  centerX: number;
  centerY: number;
}

const DEFAULT_BLOCK_W = 200;
const DEFAULT_BLOCK_H = 80;
const COLLAPSED_GROUP_W = 200;
const COLLAPSED_GROUP_H = 70;

/**
 * Beräkna bounding-box över alla extrempunkter. Inkluderar block-halv-
 * dimensioner + collapsed-group-card + expanded-group-bounds. Returnerar
 * null om inget renderas (tom canvas).
 */
export function computeContentBounds(
  blocks: WorkflowBlock[],
  groups: BlockGroup[],
  blockDimensions: Record<string, BlockDimensions> = {},
): ContentBounds | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let count = 0;

  for (const b of blocks) {
    const dim = blockDimensions[b.id];
    const w = dim?.width ?? DEFAULT_BLOCK_W;
    const h = dim?.height ?? DEFAULT_BLOCK_H;
    minX = Math.min(minX, b.x - w / 2);
    minY = Math.min(minY, b.y - h / 2);
    maxX = Math.max(maxX, b.x + w / 2);
    maxY = Math.max(maxY, b.y + h / 2);
    count++;
  }

  for (const g of groups) {
    if (g.collapsed) {
      const cx = g.collapsedX ?? 0;
      const cy = g.collapsedY ?? 0;
      minX = Math.min(minX, cx - COLLAPSED_GROUP_W / 2);
      minY = Math.min(minY, cy - COLLAPSED_GROUP_H / 2);
      maxX = Math.max(maxX, cx + COLLAPSED_GROUP_W / 2);
      maxY = Math.max(maxY, cy + COLLAPSED_GROUP_H / 2);
      count++;
    } else if (g.boundsLeft != null && g.boundsTop != null && g.boundsRight != null && g.boundsBottom != null) {
      // Expanded group med beräknade bounds — använd dem direkt.
      minX = Math.min(minX, g.boundsLeft);
      minY = Math.min(minY, g.boundsTop);
      maxX = Math.max(maxX, g.boundsRight);
      maxY = Math.max(maxY, g.boundsBottom);
      count++;
    }
  }

  if (count === 0 || !isFinite(minX)) return null;

  return {
    minX, minY, maxX, maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
}

export interface FitResult {
  /** Scale som ska sättas på contentScale. Klampad inom [zoomMin, zoomMax]. */
  scale: number;
  /**
   * Scroll-target i WORLD-koordinater (canvas-coord-system före transform).
   * Caller konverterar till viewport-scroll: scrollLeft = (centerX - viewportW/2/scale).
   */
  scrollTo: { x: number; y: number };
  /** True om scale clamp:ades och innehållet inte ryms (user måste scrolla). */
  clamped: boolean;
}

export interface FitToScreenOptions {
  zoomMin: number;
  zoomMax: number;
  /** Padding som andel av content-bounds. Default 5%. */
  padding?: number;
}

/**
 * Beräkna scale + scroll-target för "fit to screen". Klampar ALLTID
 * inom [zoomMin, zoomMax] — får aldrig bryta tier-range. Om innehållet
 * inte ryms vid zoomMin: scale = zoomMin och scroll-target = centroid
 * (user scrollar resten via vanlig pan).
 */
export function fitToScreen(
  bounds: ContentBounds | null,
  viewport: { width: number; height: number },
  options: FitToScreenOptions,
): FitResult | null {
  if (!bounds || viewport.width <= 0 || viewport.height <= 0) return null;
  const { zoomMin, zoomMax } = options;
  const padding = options.padding ?? 0.05;

  const requiredScale = Math.min(
    viewport.width / (bounds.width * (1 + padding)),
    viewport.height / (bounds.height * (1 + padding)),
  );

  const clamped = requiredScale < zoomMin || requiredScale > zoomMax;
  const scale = Math.max(zoomMin, Math.min(zoomMax, requiredScale));

  return {
    scale,
    scrollTo: { x: bounds.centerX, y: bounds.centerY },
    clamped,
  };
}
